import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Crown, Users, FileText, Image, MessageSquare, BarChart, Trash2, CreditCard, Target, Settings, Brain, FileSearch, Clock, Activity, FlaskConical, AlertCircle, Dices, Mail } from 'lucide-react';
import { AdminService } from '@/core/services/AdminService';
import { useAuth } from '@/hooks/useAuth';
import { AdminFilterProvider, useAdminFilter } from '@/contexts/AdminFilterContext';
import { UserTypeFilterBar } from './UserTypeFilterBar';

// Inner component to use the context
const AdminLayoutContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { userTypeFilter, setUserTypeFilter } = useAdminFilter();

  // Verificar roles do usuário
  useEffect(() => {
    const checkRoles = async () => {
      if (!user?.id) return;
      
      const [adminStatus, moderatorStatus] = await Promise.all([
        AdminService.isAdmin(user.id),
        AdminService.isModerator(user.id)
      ]);
      
      setIsAdmin(adminStatus);
      setIsModerator(moderatorStatus);
      setIsLoading(false);
    };
    
    checkRoles();
  }, [user?.id]);

  // Todas as seções disponíveis
  const allSections = [
    { id: 'overview', label: 'Overview', icon: BarChart, path: '/main/joia/admin/overview', moderatorAccess: true },
    { id: 'pendencias', label: 'Pendências', icon: AlertCircle, path: '/main/joia/admin/pendencias', moderatorAccess: true },
    { id: 'users', label: 'Usuários', icon: Users, path: '/main/joia/admin/users', moderatorAccess: true },
    { id: 'documents', label: 'Documentos', icon: FileText, path: '/main/joia/admin/documents', moderatorAccess: true },
    { id: 'media', label: 'Mídia', icon: Image, path: '/main/joia/admin/media', moderatorAccess: true },
    { id: 'audit', label: 'Auditoria', icon: FileSearch, path: '/main/joia/admin/audit', moderatorAccess: true },
    { id: 'email-templates', label: 'Templates de E-mail', icon: MessageSquare, path: '/main/joia/admin/email-templates', moderatorAccess: false },
    { id: 'email-logs', label: 'Central de E-mails', icon: Mail, path: '/main/joia/admin/email-logs', moderatorAccess: false },
    { id: 'photo-migration', label: 'Migração de Fotos', icon: Image, path: '/main/joia/admin/photo-migration', moderatorAccess: false },
    { id: 'cleanup', label: 'Limpeza', icon: Trash2, path: '/main/joia/admin/cleanup', moderatorAccess: false },
    { id: 'feedbacks', label: 'Feedbacks', icon: MessageSquare, path: '/main/joia/admin/feedbacks', moderatorAccess: false },
    { id: 'plans', label: 'Planos', icon: CreditCard, path: '/main/joia/admin/plans', moderatorAccess: false },
    { id: 'missions', label: 'Missões', icon: Target, path: '/main/joia/admin/missions', moderatorAccess: false },
    { id: 'wheel', label: 'Roleta', icon: Dices, path: '/main/joia/admin/wheel', moderatorAccess: false },
    { id: 'cron-logs', label: 'Cron Jobs', icon: Clock, path: '/main/joia/admin/cron-logs', moderatorAccess: false },
    { id: 'telemetry', label: 'Telemetria', icon: Activity, path: '/main/joia/admin/telemetry', moderatorAccess: false },
    { id: 'business', label: 'Mapa de Regras', icon: Settings, path: '/main/joia/admin/business', moderatorAccess: false },
    { id: 'ai', label: 'IA', icon: Brain, path: '/main/joia/admin/ai', moderatorAccess: false },
    { id: 'tests', label: 'Testes', icon: FlaskConical, path: '/main/joia/admin/tests', moderatorAccess: false },
  ];

  // Filtrar seções baseado na role
  const sections = isAdmin 
    ? allSections 
    : allSections.filter(section => section.moderatorAccess);

  // Redirecionar moderadores se tentarem acessar páginas restritas
  useEffect(() => {
    if (!isLoading && isModerator && !isAdmin) {
      const currentSection = allSections.find(section => location.pathname === section.path);
      if (currentSection && !currentSection.moderatorAccess) {
        navigate('/main/joia/admin/overview', { replace: true });
      }
    }
  }, [location.pathname, isAdmin, isModerator, isLoading, navigate]);

  const currentSection = sections.find(section => location.pathname === section.path);
  const currentSectionLabel = currentSection?.label || 'Overview';
  const showFilter = currentSection?.moderatorAccess || false;

  const handleSectionChange = (sectionPath: string) => {
    navigate(sectionPath);
  };

  return (
    <div className="bg-gradient-to-br from-black via-gray-900 to-black text-white">
      {/* Compact Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-3">
          {/* Title and Dropdown in one line */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Crown className="h-6 w-6 text-luxury-gold" />
              <h1 className="text-lg sm:text-xl font-bold text-white">Painel Admin</h1>
            </div>

            {/* Compact Dropdown - Remove icon from trigger to avoid duplication */}
            <div className="min-w-[140px] sm:min-w-[180px]">
              <Select value={location.pathname} onValueChange={handleSectionChange}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder={currentSectionLabel} />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-white/20 text-white z-[60]">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <SelectItem 
                        key={section.id} 
                        value={section.path}
                        className="focus:bg-luxury-red/20 focus:text-white"
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span>{section.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Global User Type Filter - Moved inside header */}
        {showFilter && (
          <UserTypeFilterBar 
            value={userTypeFilter} 
            onChange={setUserTypeFilter}
            className="border-t-0"
          />
        )}
      </div>

      {/* Main Content with proper spacing for fixed header and scroll */}
      <div className={showFilter ? "pt-[120px] min-h-screen" : "pt-20 min-h-screen"}>
        <div className="pb-24 md:pb-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

// Main component with provider
const AdminLayout = () => {
  return (
    <AdminFilterProvider>
      <AdminLayoutContent />
    </AdminFilterProvider>
  );
};

export default AdminLayout;
